"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Smartphone, ChartBar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Produção de Vídeo",
  description: "Vídeos estratégicos que posicionam sua marca e aumentam a conversão de clientes.",
  icon: Smartphone,
  accentClass: "from-primary/10 to-transparent",
  iconColor: "text-primary",
  delayClass: "mt-0",
  animationDelay: "delay-100"
}, {
  title: "Estúdio de Podcast",
  description: "Estrutura profissional para criar conteúdos que fortalecem sua autoridade e geram oportunidades.",
  icon: ShieldCheck,
  accentClass: "from-primary/10 to-transparent",
  iconColor: "text-primary",
  delayClass: "lg:mt-16",
  animationDelay: "delay-200"
}, {
  title: "Tráfego Pago",
  description: "Campanhas otimizadas para atrair clientes qualificados e acelerar seus resultados.",
  icon: ChartBar,
  accentClass: "from-primary/10 to-transparent",
  iconColor: "text-primary",
  delayClass: "lg:mt-32",
  animationDelay: "delay-300"
}];
export default function PourquoiChoisirAfricaGold() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="225022" id="produtora-de-vídeo-e-estúdio-de-podcast-em-são-paulo" className="bg-secondary text-foreground px-4 sm:px-6 lg:px-8 relative overflow-hidden section" style={{
    ['--hero-padding-vertical' as any]: '160px',
    ['--hero-decor-offset' as any]: '80px',
    ['--section-vertical-padding' as any]: '160px',
    paddingTop: 'var(--section-vertical-padding)',
    paddingBottom: 'var(--section-vertical-padding)'
  }}>
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto max-w-7xl relative z-10">
        {}
        <div ref={headerReveal.ref} className={cn("mb-16 md:mb-24 max-w-3xl transition-all duration-1000 ease-out", headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-sm font-mono tracking-widest text-primary uppercase mb-4">
            Posicionamento Estratégico
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight mb-6 leading-tight">Produtora de vídeo e <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/70" data-ast-id="f:/src/pages/Home/sections/PourquoiChoisirAfricaGold.tsx|sig:vtbrfh" data-ast-start="3888" data-ast-end="4005">estúdio de podcast</span>&nbsp;para empresas que querem crescer em São Paulo.</h3>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed font-default">Vídeos, podcast e tráfego pagos integrados para gerar autoridade, atrair clientes e aumentar seu faturamento.</p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
          {features.map((feature, i) => {
          const cardReveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.2
          });
          return <div key={i} data-index={i} ref={cardReveal.ref} className={cn("relative group transition-all duration-1000 ease-out", feature.delayClass, feature.animationDelay, cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
                <Card className="bg-background backdrop-blur-none border-border shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-500 h-full overflow-hidden relative z-10 rounded-2xl">
                  {}
                  <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none", feature.accentClass)} />
                  
                  <CardHeader className="relative z-20 pb-4">
                    <div className="size-16 rounded-2xl bg-secondary border border-border/50 flex items-center justify-center mb-6 shadow-none relative overflow-hidden group-hover:scale-105 transition-transform duration-500 ease-out">
                      {}
                      <feature.icon className={cn("size-8 stroke-[2]", feature.iconColor)} />
                      {}
                      <div className={cn("absolute inset-0 opacity-10 blur-xl bg-gradient-to-br", feature.accentClass)} />
                    </div>
                    <CardTitle className="text-2xl font-default font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative z-20">
                    <p className="text-muted-foreground leading-relaxed text-base font-default">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}